import { type MutableRefObject, useEffect } from "react";
import { Scorm12API } from "scorm-again";

import { type InteractionBuffer, type LessonStatus, wrapApiMethods } from "./scormUtils";

export interface ScormApiRef {
  getValue: (element: string) => string;
}

interface UseScormApiParams {
  scormApiRef: MutableRefObject<ScormApiRef | undefined>;
  courseId: string;
  requirementId: string;
  learnerId: string;
  learnerFirstName: string;
  learnerLastName: string;
  enabled: boolean;
  onStatusChange: (status: LessonStatus) => void;
  onCourseLoaded: () => void;
}

export function useScormApi(params: UseScormApiParams): void {
  const {
    scormApiRef,
    courseId,
    requirementId,
    learnerId,
    learnerFirstName,
    learnerLastName,
    enabled,
    onStatusChange,
    onCourseLoaded,
  } = params;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const api12 = new Scorm12API({ logLevel: 4, autocommit: true });

    api12.loadFromJSON({
      core: {
        student_id: learnerId,
        student_name: `${learnerLastName}, ${learnerFirstName}`,
      },
    });

    scormApiRef.current = {
      getValue: (element: string) => api12.LMSGetValue(element),
    };

    const interactionBuffers = new Map<string, InteractionBuffer>();

    const scorm12Methods = [
      "LMSInitialize",
      "LMSFinish",
      "LMSGetValue",
      "LMSSetValue",
      "LMSCommit",
      "LMSGetLastError",
      "LMSGetErrorString",
      "LMSGetDiagnostic",
    ] as const;

    wrapApiMethods({
      api: api12 as unknown as Record<string, (...parameters: string[]) => string>,
      methods: scorm12Methods,
      courseId,
      requirementId,
      onStatusChange,
      onCourseLoaded,
      interactionBuffers,
    });

    const windowWithApi = window as unknown as Record<string, unknown>;
    windowWithApi.API = api12;

    return () => {
      if (windowWithApi.API === api12) {
        delete windowWithApi.API;
      }
      scormApiRef.current = undefined;
    };
  }, [
    enabled,
    courseId,
    requirementId,
    learnerId,
    learnerFirstName,
    learnerLastName,
    onStatusChange,
    onCourseLoaded,
    scormApiRef,
  ]);
}
